/**
 * Parsing/validation for the customer-specific minimum order value amount.
 *
 * The Settings form holds the MOV amount as a major-unit string (e.g. "1000"
 * or "99.99") and the backend expects a positive integer in minor units. This
 * helper rejects malformed input (letters, multiple decimal points, empty,
 * zero/negative, too many fractional digits) instead of silently coercing it —
 * a partial/garbage amount must keep the field dirty and retryable, never
 * masquerade as a saved value.
 *
 * The number of fractional digits allowed and the minor-unit scale both come
 * from the shared per-currency ISO-4217 minor-unit map
 * (`getCurrencyDecimalPlaces` in `@choco-admin/utils`), so a non-2-decimal
 * currency (JPY = 0, BHD = 3) is validated and written at the correct scale.
 */

import { getCurrencyDecimalPlaces } from '@choco-admin/utils';

export class InvalidMovAmountError extends Error {
  constructor(
    message = 'Enter a valid minimum order value amount (e.g. 1000 or 99.99).'
  ) {
    super(message);
    this.name = 'InvalidMovAmountError';
  }
}

/** A positive amount with at most `decimalPlaces` fractional digits. */
const buildAmountPattern = (decimalPlaces: number): RegExp =>
  decimalPlaces > 0
    ? new RegExp(`^\\d+(\\.\\d{1,${decimalPlaces}})?$`)
    : /^\d+$/;

/** A clear, currency-aware message for a malformed amount. */
const malformedAmountMessage = (decimalPlaces: number): string => {
  if (decimalPlaces === 0) {
    return 'This currency has no minor units — enter a whole-number amount (e.g. 1000).';
  }
  const example = decimalPlaces === 3 ? '1.234' : '99.99';
  return `Enter a valid amount with at most ${decimalPlaces} decimal places (e.g. ${example}).`;
};

/**
 * Converts a major-unit amount string to a positive integer in minor units,
 * scaled by the currency's minor-unit decimals (default 2 for unknown codes).
 *
 * @throws {InvalidMovAmountError} when the string is not a well-formed positive
 * amount for the currency, so callers can treat it as a failed operation.
 */
export const parseMovAmountToMinorUnits = (
  rawAmount: string,
  currency?: string | null
): number => {
  const trimmed = rawAmount.trim();
  const decimalPlaces = getCurrencyDecimalPlaces(currency?.trim() ?? '');

  if (!buildAmountPattern(decimalPlaces).test(trimmed)) {
    throw new InvalidMovAmountError(malformedAmountMessage(decimalPlaces));
  }

  const majorUnits = Number(trimmed);
  if (!Number.isFinite(majorUnits) || majorUnits <= 0) {
    throw new InvalidMovAmountError(
      'Minimum order value amount must be greater than zero.'
    );
  }

  const minorUnits = Math.round(majorUnits * 10 ** decimalPlaces);
  if (!Number.isInteger(minorUnits) || minorUnits <= 0) {
    throw new InvalidMovAmountError();
  }

  return minorUnits;
};

/** The MOV write a save should perform, derived from the two form fields. */
export type CustomerLevelMovOperation =
  | { action: 'remove' }
  | { action: 'update'; amount: number; currency: string };

/**
 * Resolves the customer-specific MOV form fields into a save operation.
 *
 * Amount and currency are treated as a pair:
 *  - both empty  → remove the override (fall back to the supplier default)
 *  - both set    → update with the amount converted to minor units
 *  - half-filled → {@link InvalidMovAmountError} (both are required)
 *  - malformed   → {@link InvalidMovAmountError} (never silently coerced)
 */
export const resolveCustomerLevelMov = (
  rawAmount: string | null | undefined,
  rawCurrency: string | null | undefined
): CustomerLevelMovOperation => {
  const amount = rawAmount?.trim() ?? '';
  const currency = rawCurrency?.trim() ?? '';

  if (!amount && !currency) {
    return { action: 'remove' };
  }

  if (!amount || !currency) {
    throw new InvalidMovAmountError(
      'Set both a currency and an amount for the minimum order value, or clear both.'
    );
  }

  return {
    action: 'update',
    // Scaled by the currency's minor-unit decimals, resolved from the shared
    // ISO-4217 map (`getCurrencyDecimalPlaces` in `@choco-admin/utils`) — a
    // non-2-decimal currency (JPY = 0, BHD = 3) is written at the right scale.
    amount: parseMovAmountToMinorUnits(amount, currency),
    currency,
  };
};
